import React, { useState, useRef } from 'react';
import './App.css';

// Import images
import academyCrest from './images/academy-crest.png';
import butterflyGold from './images/butterfly-gold.png';
import seleneaCrest from './images/selenea-crest.png';
import aureliusCrest from './images/aurelius-crest.png';
import verdanianCrest from './images/verdanian-crest.png';
import rubiconCrest from './images/rubicon-crest.png';
import amethystCrest from './images/amethyst-crest.png';

// Quiz questions with YA voice
const questions = [
  {
    id: 1,
    text: "You find a locked door in the academy that no one else seems to notice. Do you:",
    options: [
      { text: "Watch who comes and goes, noting patterns", house: "Aurelius" },
      { text: "Try to figure out the lock mechanism", house: "Selenea" },
      { text: "Assume it's locked for a reason and move on", house: "Rubicon" },
      { text: "Ask around to see if anyone knows what's behind it", house: "Verdanian" },
      { text: "Put your hand on it and see what happens", house: "Amethyst" }
    ]
  },
  {
    id: 2,
    text: "There's a student who breaks curfew every night. Everyone knows but no one's caught them. You:",
    options: [
      { text: "Wonder why faculty haven't noticed the pattern yet", house: "Aurelius" },
      { text: "Respect the execution—that takes skill", house: "Selenea" },
      { text: "Mind your own business—not your problem", house: "Rubicon" },
      { text: "Lowkey think they're kind of a legend", house: "Verdanian" },
      { text: "Wish you'd thought of it first", house: "Amethyst" }
    ]
  },
  {
    id: 3,
    text: "You're practicing a glyph and your magic just went completely sideways. You:",
    options: [
      { text: "Try to figure out exactly where the pattern broke down", house: "Aurelius" },
      { text: "Experiment with what would happen if you changed one line", house: "Selenea" },
      { text: "Start over and practice it correctly until you get it right", house: "Rubicon" },
      { text: "Lean into it and act like the chaos was intentional", house: "Verdanian" },
      { text: "See if you can make it do something even weirder next time", house: "Amethyst" }
    ]
  },
  {
    id: 4,
    text: "You have to give a speech about why you deserve your scholarship. You:",
    options: [
      { text: "Present facts about your qualifications and background", house: "Aurelius" },
      { text: "Talk about how you'll contribute new ideas to the academy", house: "Selenea" },
      { text: "Emphasize your work ethic and commitment", house: "Rubicon" },
      { text: "Tell a compelling story that makes them remember you", house: "Verdanian" },
      { text: "Be honest about why their system is broken and you're proof", house: "Amethyst" }
    ]
  },
  {
    id: 5,
    text: "An Upper Cantara student calls your magic \"crude\" and \"unrefined.\" You:",
    options: [
      { text: "Ask them to define what they mean by \"refined\"", house: "Aurelius" },
      { text: "Show them how your \"crude\" magic gets better results", house: "Selenea" },
      { text: "Ignore them—proving yourself isn't worth your energy", house: "Rubicon" },
      { text: "Turn it into a teaching moment about class prejudice", house: "Verdanian" },
      { text: "Let your magic do the talking (loudly)", house: "Amethyst" }
    ]
  }
];

// House data with crest images and custom share text
const houses = {
  Selenea: {
    name: "SELENEA HOUSE",
    subtitle: "Innovation • Control • Optimization",
    color: "#c0c0c0",
    crest: seleneaCrest,
    description: "You've been designated for Selenea House—students who transform chaos into precision, who make wild magic predictable and useful.\n\nThe academy sees in you the capacity to innovate within acceptable parameters. To push boundaries while maintaining control. To take what is unpredictable and render it safe for institutional purposes.\n\nYou will learn to optimize. To refine. To make magic serve.",
    shareText: "The Stone chose innovation. ✨\n\nI turn wild magic into breakthroughs.\nCreate what others can't imagine.\n\nCantara Academy needs minds that push boundaries.\n\nWhat does the Stone see in you?\n\ncantara.academy",
    shareHashtags: "ThirteenthCagebreaker,CanataraAcademy,SeleneaHouse,YAFantasy"
  },
  Aurelius: {
    name: "AURELIUS HOUSE",
    subtitle: "Observation • Analysis • Documentation",
    color: "#ffd700",
    crest: aureliusCrest,
    description: "You've been designated for Aurelius House—students who watch, who document, who see patterns others miss.\n\nThe academy recognizes in you keen observation and analytical precision. You will be trained to identify what requires scrutiny, to recognize when individual expressions of magic warrant closer institutional attention.\n\nYou are the academy's eyes. You will learn what deserves watching.",
    shareText: "Academic excellence. 👁️\n\nThe Stone chose precision of mind.\n\nI analyze. I document. I understand.\n\nCantara Academy trains minds like mine.\n\nWhat does the Stone see in you?\n\ncantara.academy",
    shareHashtags: "ThirteenthCagebreaker,CanataraAcademy,AureliusHouse,YAFantasy"
  },
  Rubicon: {
    name: "RUBICON HOUSE",
    subtitle: "Service • Dedication • Foundation",
    color: "#9b111e",
    crest: rubiconCrest,
    description: "You've been designated for Rubicon House—students who build what others envision, who maintain what others design, who work without needing recognition.\n\nThe academy values your dedication, your reliability, your capacity for sustained effort. You are the hands that execute, the strength that endures, the foundation upon which excellence is built.\n\nTrue purpose lies not in recognition, but in work completed.",
    shareText: "The foundation. 💪\n\nI create what sustains.\nBuild what endures.\n\nThe Stone recognizes those who serve with excellence.\n\nWhat would it recognize in you?\n\ncantara.academy",
    shareHashtags: "ThirteenthCagebreaker,CanataraAcademy,RubiconHouse,YAFantasy"
  },
  Verdanian: {
    name: "VERDANIAN HOUSE",
    subtitle: "Performance • Persuasion • Inspiration",
    color: "#50c878",
    crest: verdanianCrest,
    description: "You've been designated for Verdanian House—students who perform, who inspire, who make compliance sound like choice and cages look like homes.\n\nThe academy recognizes your charisma, your artistic sensibility, your refined capacity to move others toward institutional objectives. You are the academy's voice.\n\nYou will learn to make beauty from discipline, to present policy as inspiration, to help others embrace their designated roles.",
    shareText: "The Stone saw artistry. 🎭\n\nI transform discipline into performance.\nMake magic into spectacle.\n\nCantara Academy chose me to inspire.\n\nWould it choose you?\n\ncantara.academy",
    shareHashtags: "ThirteenthCagebreaker,CanataraAcademy,VerdanianHouse,YAFantasy"
  },
  Amethyst: {
    name: "AMETHYST DESIGNATION",
    subtitle: "Uncategorizable • Powerful • Dangerous",
    color: "#9370db",
    crest: amethystCrest,
    description: "If you were at Cantara Academy, the Designating Stone would struggle to place you.\n\nYour magic wouldn't fit their houses. It wouldn't follow their rules. It would refuse to be contained.\n\nThere have been thirteen amethyst designations across three hundred years:\n\nCagebreakers I through XI—now keystones, contained in the Vault.\n\nCagebreaker XII—Lark Kettler, who escaped. Roe's mother.\n\nCagebreaker XIII—Roe Kettler, who's breaking every cage they build.\n\nYou share their traits.\n\nThe academy would call you dangerous.\n\nThe Cagebreakers call you family.\n\nWelcome to the resistance.",
    shareText: "The Designating Stone couldn't place me. 🦋\n\nThirteen came before.\nUncategorizable. Uncontainable. Dangerous.\n\nThe academy tried to sort me.\nI refused to fit.\n\nWhat would the Stone see in you?\n\ncantara.academy",
    shareHashtags: "ThirteenthCagebreaker,CanataraAcademy,Amethyst,Cagebreaker,YAFantasy"
  }
};

function App() {
  const [stage, setStage] = useState('welcome');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showLetter, setShowLetter] = useState(false);
  const resultCardRef = useRef(null);

  const handleStart = () => {
    setStage('quiz');
    setCurrentQuestion(0);
    setAnswers([]);
  };

  const handleAnswer = (house) => {
    const newAnswers = [...answers, house];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setStage('calculating');
      setTimeout(() => {
        const finalHouse = calculateHouse(newAnswers);
        setResult(finalHouse);
        setStage('results');
        // Update page meta tags for social sharing
        updateMetaTags(finalHouse);
      }, 3000);
    }
  };

  const calculateHouse = (answersList) => {
    const counts = {};
    answersList.forEach(house => {
      counts[house] = (counts[house] || 0) + 1;
    });
    
    // Amethyst is RARE - requires clear majority (3+ out of 5)
    const amethystCount = counts['Amethyst'] || 0;
    if (amethystCount >= 3) {
      return 'Amethyst';
    }
    
    // For other houses, find the highest count (excluding Amethyst)
    const priorityOrder = ['Selenea', 'Aurelius', 'Verdanian', 'Rubicon'];
    
    let maxCount = 0;
    let resultHouse = 'Rubicon'; // Default fallback
    
    Object.entries(counts).forEach(([house, count]) => {
      if (house === 'Amethyst') return; // Skip Amethyst in regular calculation
      
      if (count > maxCount) {
        maxCount = count;
        resultHouse = house;
      } else if (count === maxCount) {
        // In case of tie, use priority order (never gives Amethyst)
        if (priorityOrder.indexOf(house) < priorityOrder.indexOf(resultHouse)) {
          resultHouse = house;
        }
      }
    });
    
    return resultHouse;
  };

  // Update meta tags for social sharing
  const updateMetaTags = (houseName) => {
    const house = houses[houseName];
    const url = 'https://cantara.academy';
    
    // Update Open Graph tags
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', `${house.name} - Cantara Academy`);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', house.shareText);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', url);
    
    // Update Twitter tags
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', `${house.name} - Cantara Academy`);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', house.shareText);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading state
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Subscribing...';
    submitButton.disabled = true;
    
    try {
      // Send to your Hostinger PHP endpoint
      const response = await fetch('https://cantara.academy/api/mailerlite-signup.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: name,
          house: result
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowLetter(true);
        console.log('Successfully subscribed:', data);
      } else {
        alert('There was an issue with your subscription. Please try again.');
        console.error('Subscription error:', data);
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
      console.error('Fetch error:', error);
    } finally {
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  };

  const handleRetake = () => {
    setStage('welcome');
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
    setEmail('');
    setName('');
    setShowLetter(false);
  };

  // Enhanced social sharing functions with personalized text
  const shareOnTwitter = () => {
    const house = houses[result];
    const text = encodeURIComponent(house.shareText);
    const url = encodeURIComponent('https://cantara.academy');
    const hashtags = encodeURIComponent(house.shareHashtags);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${hashtags}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent('https://cantara.academy');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareOnPinterest = () => {
    const house = houses[result];
    const url = encodeURIComponent('https://cantara.academy');
    const description = encodeURIComponent(house.shareText);
    window.open(`https://pinterest.com/pin/create/button/?url=${url}&description=${description}`, '_blank');
  };

  const copyShareText = () => {
    const house = houses[result];
    const shareText = `${house.shareText}\n\n#${house.shareHashtags.replace(/,/g, ' #')}`;
    navigator.clipboard.writeText(shareText).then(() => {
      alert('✨ Share text copied to clipboard!\n\nPaste it anywhere to share your designation! 🦋');
    });
  };

  // Download result card as image
  const downloadResultCard = () => {
    const house = houses[result];
    
    // Create a canvas to draw the result card
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (Instagram square format)
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Create darker, more dramatic gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#050812');
    gradient.addColorStop(0.5, '#0a0e27');
    gradient.addColorStop(1, '#050812');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add subtle texture overlay
    for (let i = 0; i < 200; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = Math.random() * 2;
      ctx.fillStyle = `rgba(212, 175, 55, ${Math.random() * 0.1})`;
      ctx.fillRect(x, y, size, size);
    }
    
    // Load and draw the crest image
    const crestImg = new Image();
    crestImg.crossOrigin = 'anonymous';
    crestImg.onload = () => {
      // Draw dramatic border first
      ctx.strokeStyle = house.color;
      ctx.lineWidth = 6;
      ctx.shadowColor = house.color;
      ctx.shadowBlur = 30;
      ctx.strokeRect(50, 50, canvas.width - 100, canvas.height - 100);
      ctx.shadowBlur = 0;
      
      // Draw crest with strong glow
      const crestSize = 350;
      const crestX = (canvas.width - crestSize) / 2;
      const crestY = 180;
      
      // Multiple glow layers for dramatic effect
      ctx.shadowColor = house.color;
      ctx.shadowBlur = 60;
      ctx.drawImage(crestImg, crestX, crestY, crestSize, crestSize);
      ctx.shadowBlur = 40;
      ctx.drawImage(crestImg, crestX, crestY, crestSize, crestSize);
      ctx.shadowBlur = 0;
      ctx.drawImage(crestImg, crestX, crestY, crestSize, crestSize);
      
      // Draw house name with glow
      ctx.shadowColor = house.color;
      ctx.shadowBlur = 20;
      ctx.fillStyle = house.color;
      ctx.font = 'bold 54px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText(house.name, canvas.width / 2, 620);
      
      // Draw subtitle (no glow)
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#94a3b8';
      ctx.font = '28px Georgia, serif';
      ctx.fillText(house.subtitle, canvas.width / 2, 670);
      
      // Draw mysterious tagline
      ctx.fillStyle = '#cbd5e1';
      ctx.font = 'italic 32px Georgia, serif';
      ctx.fillText('The Stone has spoken.', canvas.width / 2, 780);
      
      // Draw website URL (subtle but clear)
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 30px Georgia, serif';
      ctx.fillText('cantara.academy', canvas.width / 2, 920);
      
      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `cantara-${result.toLowerCase()}-designation.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      });
    };
    
    crestImg.src = house.crest;
  };

  return (
    <div className="App">
      {/* Floating butterflies */}
      <div className="floating-butterflies">
        <img src={butterflyGold} alt="" className="butterfly butterfly-1" />
        <img src={butterflyGold} alt="" className="butterfly butterfly-2" />
        <img src={butterflyGold} alt="" className="butterfly butterfly-3" />
      </div>

      {stage === 'welcome' && (
        <div className="welcome-screen">
          <img src={academyCrest} alt="Cantara Academy" className="academy-crest-image" />
          <h1 className="academy-title">CANTARA ACADEMY</h1>
          <h2 className="ceremony-title">The Designation Ceremony</h2>
          <p className="welcome-text">
            The Designating Stone awaits. Place your hand upon its surface 
            and discover where you belong—or if you belong at all.
          </p>
          <p className="welcome-subtext">
            Answer truthfully. The Stone sees more than you think.
          </p>
          <p className="welcome-details">
            Five questions. Five designations. One that will 
            define your place at the academy.
          </p>
          <p className="welcome-question">
            Will you receive a house? Or will you be marked as something else?
          </p>
          <button className="start-button" onClick={handleStart}>
            Begin the Ceremony
          </button>
          <p className="tagline">5 questions • Your fate</p>
        </div>
      )}

      {stage === 'quiz' && (
        <div className="quiz-screen">
          <img src={academyCrest} alt="Cantara Academy" className="quiz-header-crest" />
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{width: `${((currentQuestion + 1) / questions.length) * 100}%`}}
            ></div>
          </div>
          <div className="question-number">
            Question {currentQuestion + 1} of {questions.length}
          </div>
          <h2 className="question-text">{questions[currentQuestion].text}</h2>
          <div className="options">
            {[...questions[currentQuestion].options]
              .sort(() => Math.random() - 0.5)
              .map((option, index) => (
                <button
                  key={index}
                  className="option-button"
                  onClick={() => handleAnswer(option.house)}
                >
                  {option.text}
                </button>
              ))}
          </div>
        </div>
      )}

      {stage === 'calculating' && (
        <div className="calculating-screen">
          <img src={academyCrest} alt="Designating Stone" className="stone-animation-image" />
          <p className="calculating-text">
            The Designating Stone is analyzing your essence...
          </p>
          <p className="calculating-subtext">
            Place your hand upon its surface and await judgment
          </p>
        </div>
      )}

      {stage === 'results' && result && (
        <div className="results-screen">
          <div className="result-header">
            <p className="ceremony-complete">The Ceremony is Complete</p>
            <h1 className="destiny-title">Your Destiny Awaits</h1>
          </div>
          
          <div className="house-result" style={{borderColor: houses[result].color}} ref={resultCardRef}>
            <img 
              src={houses[result].crest} 
              alt={houses[result].name}
              className="house-crest-image"
            />
            <h2 className="house-name" style={{color: houses[result].color}}>
              {houses[result].name}
            </h2>
            <p className="house-subtitle">{houses[result].subtitle}</p>
            <p className="house-description">{houses[result].description}</p>
          </div>

          {/* Social Sharing Section */}
          <div className="social-sharing">
            <p className="share-prompt">Share your designation:</p>
            
            {/* Download Result Card Button */}
            <button className="download-card-button" onClick={downloadResultCard}>
              <i className="fas fa-download"></i> Download Result Card
            </button>
            <p className="download-note">Perfect for Instagram & TikTok stories!</p>
            
            <div className="share-buttons">
              <button className="share-button share-twitter" onClick={shareOnTwitter}>
                <i className="fab fa-twitter"></i> Twitter
              </button>
              <button className="share-button share-facebook" onClick={shareOnFacebook}>
                <i className="fab fa-facebook-f"></i> Facebook
              </button>
              <button className="share-button share-pinterest" onClick={shareOnPinterest}>
                <i className="fab fa-pinterest-p"></i> Pinterest
              </button>
              <button className="share-button share-copy" onClick={copyShareText}>
                <i className="fas fa-copy"></i> Copy Share Text
              </button>
            </div>
            <p className="share-note">One-click sharing with your personalized message!</p>
          </div>

          {!showLetter ? (
            <div className="email-capture">
              <h3 className="email-title">CONFIRM YOUR DESIGNATION</h3>
              <p className="email-description">
                The Stone has spoken. Now the academy must know where to send 
                your official correspondence.
              </p>
              <form onSubmit={handleEmailSubmit} className="email-form">
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="email-input"
                />
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="email-input"
                />
                <button type="submit" className="submit-button">
                  Receive My Acceptance Letter
                </button>
              </form>
              <button className="skip-button" onClick={() => setShowLetter(true)}>
                Skip for now
              </button>
            </div>
          ) : (
            <div className="acceptance-letter">
              <h3 className="letter-title">YOUR ACCEPTANCE LETTER</h3>
              <div className="letter-content">
                <p className="letter-header">CANTARA ACADEMY<br/>Est. 1725</p>
                <p className="letter-greeting">Dear {name || 'Student'},</p>
                <p className="letter-body">
                  The Designating Stone has determined your placement: <strong>{houses[result].name}</strong>.
                </p>
                <p className="letter-body">
                  Your official designation letter will arrive in your inbox shortly.
                </p>
                <p className="letter-closing">
                  Welcome to Cantara Academy.<br/>
                  The gates are waiting.
                </p>
                <p className="letter-signature">
                  — The Office of the Registrar
                </p>
              </div>
            </div>
          )}

          <button className="retake-button" onClick={handleRetake}>
            ↻ Retake the Designation Ceremony
          </button>

          <div className="book-promo">
            <p className="book-title">THE THIRTEENTH CAGEBREAKER</p>
            <p className="book-author">by Melissa Jean Vallejos</p>
            <p className="book-date">Coming February 13, 2026</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;